import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Upload, FileText, Calendar, User, AlertCircle, Mail, RefreshCw, Send, Eye, MousePointer, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import API from '../config/api';
import { formatDateTimeShortET } from '../utils/formatters';
import { useDropZone } from '../hooks/useDropZone';

/**
 * Mail-Away Status Block - Quick Action Component
 * 
 * Displays mail-away status and provides quick actions for Team Leader/Admin:
 * - Mark Response Received (with file upload)
 * - Mark as Sent Back
 * - View response details
 * - Email status and resend functionality
 * 
 * V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
 */
const MailAwayStatusBlock = ({ orderData, currentUser, onUpdate }) => {
  const [showMarkReceivedModal, setShowMarkReceivedModal] = useState(false);
  const [showSendBackModal, setShowSendBackModal] = useState(false);
  const [showResendConfirmModal, setShowResendConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  
  // Form state for Mark Response Received
  // Feb 2026 — Multi-file Mail-Away Response (up to 3 files, 30 MB total).
  // Backed by the shared `useDropZone` hook in append-mode so click and
  // drag-drop go through one validated pipeline. The single-file
  // `responseFile` alias below keeps backward-compatible UI code paths
  // working unchanged.
  const zone = useDropZone({
    allowedTypes: ['application/pdf'],
    allowedExtensions: ['pdf'],
    maxSize: 10 * 1024 * 1024,
    multiple: true,
    appendMode: true,
    maxFiles: 3,
    maxTotalSize: 30 * 1024 * 1024,
  });
  const selectedFiles = Array.isArray(zone.file) ? zone.file : [];
  const responseFile = selectedFiles[0] || null;
  const [responseNotes, setResponseNotes] = useState('');
  const [responseDate, setResponseDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  
  // Check if user has permission (Admin or Team Leader - both Tax and Title)
  const hasPermission = 
    currentUser?.role === 'admin' || 
    currentUser?.role === 'tax_search_team_leader' ||
    currentUser?.role === 'title_search_team_leader';
  
  // Check if response has been received
  const isResponseReceived = orderData?.mail_away_received === true;
  
  // Get received info
  const receivedDate = orderData?.mailaway_received_at;
  const receivedByName = orderData?.mailaway_received_by_name;
  
  // Get response file
  const responseFiles = orderData?.files?.filter(f => f.file_type === 'mail_away_response') || [];
  const latestResponseFile = responseFiles[responseFiles.length - 1];
  
  // Get ALL mail-away related notes (including initiated, resend, received)
  // P1 FIX: Show all mail-away notes for full audit trail visibility
  const mailAwayNotes = orderData?.status_notes?.filter(n => 
    n.type === 'mail_away' || 
    n.type === 'mail_away_received' || 
    n.type === 'mail_away_resend'
  ) || [];
  const latestNote = mailAwayNotes?.[mailAwayNotes.length - 1];
  
  const fetchEmailStatus = async (orderId) => {
    try {
      const response = await axios.get(
        `${API}/api/orders/${orderId}/mailaway/email-status`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setEmailStatus(response.data);
    } catch (err) {
      console.error('Error fetching email status:', err);
      // Not critical, don't show error
    }
  };
  
  // Fetch email status when component mounts
  useEffect(() => {
    if (orderData?.id && orderData?.status === 'mail_away') {
      fetchEmailStatus(orderData.id);
    }
  }, [orderData?.id, orderData?.status]);
  
  const handleResendEmail = async () => {
    setResendLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API}/api/orders/${orderData.id}/mailaway/resend`,
        { confirm: true },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setShowResendConfirmModal(false);
      
      if (response.data.success) {
        toast.success('Mail-Away sent successfully', {
          description: `Email sent to ${response.data.recipient}`,
          duration: 4000
        });
        fetchEmailStatus(orderData.id); // Refresh email status
      } else {
        toast.error('Failed to send email', {
          description: response.data.message,
          duration: 5000
        });
      }
      
    } catch (err) {
      console.error('Error resending email:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to resend email';
      setError(errorMessage);
      toast.error('Failed to send Mail-Away', {
        description: errorMessage,
        duration: 5000
      });
    } finally {
      setResendLoading(false);
    }
  };
  
  // V1 REQUIREMENT: Use ET timezone for all timestamps
  const formatDate = formatDateTimeShortET;

  const handleMarkReceived = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one PDF file');
      return;
    }
    if (!responseNotes.trim()) {
      setError('Please enter response notes');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      // Backward-compat: first file still posts as `response_file=` so older
      // backends keep working. Additional files post as repeated `files=`.
      formData.append('response_file', selectedFiles[0]);
      for (const f of selectedFiles) {
        formData.append('files', f);
      }
      formData.append('notes', responseNotes.trim());
      formData.append('received_date', new Date(responseDate).toISOString());

      const response = await axios.post(
        `${API}/api/orders/${orderData.id}/mail-away/mark-received`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Success!
      setShowMarkReceivedModal(false);
      zone.reset();
      setResponseNotes('');

      // Trigger parent update
      if (onUpdate) {
        onUpdate();
      }

      toast.success('Response marked as received', {
        description: 'Mail-Away response has been recorded successfully',
        duration: 4000
      });
    } catch (err) {
      console.error('Error marking response received:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to mark response received';
      setError(errorMessage);
      toast.error('Failed to mark as received', {
        description: errorMessage,
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendBack = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(
        `${API}/api/orders/${orderData.id}/send-back`,
        {
          target_status: 'mail_away',  // CRITICAL: Must specify target status
          reason: 'Mail-Away sent back - response not considered completed'
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setShowSendBackModal(false);
      
      if (onUpdate) {
        onUpdate();
      }
      
      toast.success('Sent back successfully', {
        description: 'Mail-Away has been marked as sent back',
        duration: 4000
      });
      
    } catch (err) {
      console.error('Error sending back:', err);
      setError(err.response?.data?.detail || 'Failed to send back');
    } finally {
      setLoading(false);
    }
  };
  
  if (orderData?.status !== 'mail_away') {
    return null;
  }
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Mail-Away Status</h3>
        
        {/* Status Badges */}
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-cyan-100 text-cyan-800 border border-cyan-300">
            <FileText size={12} />
            Mail-Away
          </span>
          {isResponseReceived && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-300">
              <CheckCircle size={12} />
              Response Received
            </span>
          )}
        </div>
      </div>
      
      {/* Email Notification Status */}
      {emailStatus && (
        <div className={`mb-4 p-4 rounded-lg ${emailStatus.email_sent ? 'bg-blue-50 border border-blue-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-start justify-between">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Mail size={14} className={emailStatus.email_sent ? 'text-blue-600' : 'text-yellow-600'} />
                <span className="font-medium text-gray-800">
                  {emailStatus.email_sent ? 'Email Notification Sent' : 'Email Not Sent'}
                </span>
              </div>
              
              {emailStatus.email_sent && (
                <>
                  <div className="flex items-center gap-2 text-gray-600 pl-5">
                    <span>Sent to:</span>
                    <span className="font-medium">{emailStatus.recipient}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 pl-5">
                    <span>Sent at:</span>
                    <span>{formatDate(emailStatus.sent_at)}</span>
                  </div>
                  {emailStatus.resend_count > 1 && (
                    <div className="flex items-center gap-2 text-gray-500 pl-5 text-xs">
                      <span>Resent {emailStatus.resend_count - 1} time(s)</span>
                    </div>
                  )}
                </>
              )}
              
              {/* Delivery Tracking Info */}
              {emailStatus.email_sent && (
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-blue-200">
                  {/* Delivered Status */}
                  <div className="flex items-center gap-1.5">
                    {emailStatus.delivered_at || emailStatus.delivery_status === 'delivery' ? (
                      <CheckCircle size={14} className="text-green-600" />
                    ) : emailStatus.bounced ? (
                      <AlertTriangle size={14} className="text-red-500" />
                    ) : (
                      <RefreshCw size={14} className="text-gray-400" />
                    )}
                    <span className={`text-xs ${
                      emailStatus.bounced ? 'text-red-600 font-medium' :
                      emailStatus.delivered_at ? 'text-green-700' : 'text-gray-500'
                    }`}>
                      {emailStatus.bounced ? `Bounced${emailStatus.bounce_type ? ` (${emailStatus.bounce_type})` : ''}` :
                       emailStatus.delivered_at || emailStatus.delivery_status === 'delivery' ? 'Delivered' : 'Pending'}
                    </span>
                  </div>
                  
                  {/* Opened Status */}
                  <div className="flex items-center gap-1.5">
                    <Eye size={14} className={emailStatus.opened || emailStatus.open_count > 0 ? 'text-purple-600' : 'text-gray-400'} />
                    <span className={`text-xs ${emailStatus.opened || emailStatus.open_count > 0 ? 'text-purple-700' : 'text-gray-500'}`}>
                      {emailStatus.open_count > 0 ? `Opened (${emailStatus.open_count}x)` : 'Not opened'}
                    </span>
                  </div>
                  
                  {/* Clicked Status */}
                  <div className="flex items-center gap-1.5">
                    <MousePointer size={14} className={emailStatus.click_count > 0 ? 'text-blue-600' : 'text-gray-400'} />
                    <span className={`text-xs ${emailStatus.click_count > 0 ? 'text-blue-700' : 'text-gray-500'}`}>
                      {emailStatus.click_count > 0 ? `Clicked (${emailStatus.click_count}x)` : 'No clicks'}
                    </span>
                  </div>
                </div>
              )}
              
              {!emailStatus.email_sent && (
                <p className="text-gray-600 pl-5 text-xs">
                  No recipient email configured or email service unavailable
                </p>
              )}
            </div>
            
            {/* Resend Button (Admin/Team Leader only) */}
            {hasPermission && emailStatus.email_sent && (
              <button
                onClick={() => setShowResendConfirmModal(true)}
                disabled={resendLoading}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors disabled:opacity-50"
              >
                {resendLoading ? (
                  <RefreshCw size={12} className="animate-spin" />
                ) : (
                  <Send size={12} />
                )}
                Resend Email
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Response Info (when received) */}
      {isResponseReceived && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="space-y-2 text-sm">
            {/* Received Date */}
            {receivedDate && (
              <div className="flex items-center gap-2 text-gray-700">
                <Calendar size={14} className="text-green-600" />
                <span className="font-medium">Received:</span>
                <span>{formatDate(receivedDate)}</span>
              </div>
            )}
            
            {/* Received By */}
            {receivedByName && (
              <div className="flex items-center gap-2 text-gray-700">
                <User size={14} className="text-green-600" />
                <span className="font-medium">By:</span>
                <span>{receivedByName}</span>
              </div>
            )}
            
            {/* Response File */}
            {latestResponseFile && (
              <div className="flex items-center gap-2 text-gray-700">
                <FileText size={14} className="text-green-600" />
                <span className="font-medium">File:</span>
                <a
                  href={`${API}/api/files/${latestResponseFile.id}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {latestResponseFile.display_name || latestResponseFile.filename}
                </a>
              </div>
            )}
            
            {/* Notes Preview */}
            {latestNote && (
              <div className="mt-3 pt-3 border-t border-green-200">
                <p className="font-medium text-gray-700 mb-1">Notes:</p>
                <p className="text-gray-600 text-sm">
                  {latestNote.message.length > 150
                    ? `${latestNote.message.substring(0, 150)}... `
                    : latestNote.message}
                  {latestNote.message.length > 150 && (
                    <button className="text-blue-600 hover:text-blue-800 text-xs">
                      View full details
                    </button>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Mail-Away Notes History - P1 FIX: Show all notes for audit trail */}
      {mailAwayNotes && mailAwayNotes.length > 0 && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <FileText size={14} className="text-gray-600" />
            Mail-Away Notes History ({mailAwayNotes.length})
          </h4>
          <div className="space-y-3 max-h-48 overflow-y-auto">
            {mailAwayNotes.map((note, idx) => (
              <div key={idx} className="text-sm border-l-2 border-gray-300 pl-3 py-1">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    note.type === 'mail_away' ? 'bg-cyan-100 text-cyan-700' :
                    note.type === 'mail_away_resend' ? 'bg-orange-100 text-orange-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {note.type === 'mail_away' ? 'Initiated' : 
                     note.type === 'mail_away_resend' ? 'Resent' : 'Received'}
                  </span>
                  <span>{note.created_by_name || 'System'}</span>
                  <span>•</span>
                  <span>{formatDateTimeShortET(note.created_at)}</span>
                  {note.jurisdiction_name && (
                    <>
                      <span>•</span>
                      <span className="font-medium">{note.jurisdiction_name}</span>
                    </>
                  )}
                </div>
                <p className="text-gray-700">{note.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Quick Action Buttons */}
      {hasPermission && (
        <div className="flex items-center gap-3">
          {!isResponseReceived ? (
            <button
              onClick={() => setShowMarkReceivedModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <CheckCircle size={16} />
              Mark Response Received
            </button>
          ) : (
            <>
              <button
                onClick={() => setShowSendBackModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors"
              >
                <XCircle size={16} />
                Mark as Sent Back
              </button>
              {latestResponseFile && (
                <a
                  href={`${API}/api/files/${latestResponseFile.id}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <FileText size={16} />
                  View Response
                </a>
              )}
            </>
          )}
        </div>
      )}
      
      {/* No Permission Message */}
      {!hasPermission && isResponseReceived && (
        <p className="text-sm text-gray-500 italic">
          Only Team Leaders and Admins can modify mail-away status
        </p>
      )}
      
      {/* Mark Response Received Modal */}
      {showMarkReceivedModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={() => { if (!loading) { setShowMarkReceivedModal(false); zone.reset(); setError(null); } }} />
            
            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Mark Mail-Away Response Received
              </h3>
              
              {(error || zone.error) && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error || zone.error}</p>
                </div>
              )}
              
              <div className="space-y-4">
                {/* File Upload (multi-file, up to 3, 30 MB total) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Response PDF <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    PDF only • Maximum 10 MB per file • Up to 3 files • 30 MB total
                  </p>

                  {selectedFiles.length < 3 && (
                    <label
                      {...zone.dropzoneProps}
                      className={`flex flex-col items-center justify-center w-full px-6 py-6
                                 border-2 ${zone.isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} border-dashed rounded-lg cursor-pointer
                                 hover:border-blue-500 hover:bg-blue-50 transition-all group`}
                      data-testid="mailaway-dropzone"
                    >
                      <Upload size={28} className={`${zone.isDragging ? 'text-blue-500' : 'text-gray-400'} group-hover:text-blue-500 mb-2 transition-colors`} />
                      <span className={`text-sm font-medium ${zone.isDragging ? 'text-blue-600' : 'text-gray-600'} group-hover:text-blue-600 transition-colors`}>
                        {zone.isDragging
                          ? 'Drop file(s) here'
                          : selectedFiles.length === 0
                            ? 'Click to upload file(s)'
                            : `Add more files (${selectedFiles.length}/3 selected)`}
                      </span>
                      <span className="text-xs text-gray-500 mt-1">or drag and drop</span>
                      <input {...zone.inputProps} data-testid="mailaway-file-input" />
                    </label>
                  )}

                  {selectedFiles.length > 0 && (
                    <div className={`space-y-2 ${selectedFiles.length < 3 ? 'mt-3' : ''}`} data-testid="mailaway-selected-files">
                      {selectedFiles.map((f, idx) => (
                        <div
                          key={`${f.name}-${idx}-${f.size}`}
                          className="flex items-center gap-3 p-3 bg-blue-50 border-2 border-blue-200 rounded-lg"
                          data-testid={`mailaway-selected-file-${idx}`}
                        >
                          <div className="flex-shrink-0 w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                            <FileText size={18} className="text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                            <p className="text-xs text-gray-500">{(f.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => zone.removeAt(idx)}
                            className="flex-shrink-0 p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Remove file"
                            data-testid={`mailaway-remove-file-${idx}`}
                          >
                            <X size={18} />
                          </button>
                        </div>
                      ))}
                      <p className="text-xs text-gray-500 text-right" data-testid="mailaway-total-size">
                        Total: {(selectedFiles.reduce((s, f) => s + (f.size || 0), 0) / 1024 / 1024).toFixed(2)} MB / 30.00 MB
                      </p>
                    </div>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Suggested filename: {orderData.order_number} - Mail-Away Response.pdf
                  </p>
                </div>
                
                {/* Response Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Response Received Date
                  </label>
                  <input
                    type="date"
                    value={responseDate}
                    onChange={(e) => setResponseDate(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes / Response Details <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={responseNotes}
                    onChange={(e) => setResponseNotes(e.target.value)}
                    rows={4}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter response details..."
                  />
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={handleMarkReceived}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Saving...' : 'Save & Mark Received'}
                </button>
                <button
                  onClick={() => { setShowMarkReceivedModal(false); zone.reset(); setError(null); }}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Send Back Confirmation Modal */}
      {showSendBackModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={() => setShowSendBackModal(false)} />
            
            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Mark as Sent Back?
              </h3>
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              
              <p className="text-sm text-gray-600 mb-6">
                This will mark the Mail-Away as sent back and remove it from &quot;Mail-Away Received&quot;. 
                The response file will remain in Files for audit purposes. Continue?
              </p>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSendBack}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Processing...' : 'Yes, Send Back'}
                </button>
                <button
                  onClick={() => setShowSendBackModal(false)}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Resend Email Confirmation Modal */}
      {showResendConfirmModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={() => setShowResendConfirmModal(false)} />
            
            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Send size={20} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Resend Mail-Away Email?
                </h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                This will send another Mail-Away notification email to the recipient. 
                The email will include the MLC Letter PDF and a secure download link.
              </p>
              
              {emailStatus?.recipient && (
                <div className="p-3 bg-gray-50 rounded-lg mb-4">
                  <div className="text-sm">
                    <span className="text-gray-500">Recipient:</span>
                    <span className="ml-2 font-medium text-gray-800">{emailStatus.recipient}</span>
                  </div>
                </div>
              )}
              
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}
              
              <div className="flex items-center gap-3">
                <button
                  onClick={handleResendEmail}
                  disabled={resendLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {resendLoading ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Resend Email
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowResendConfirmModal(false)}
                  disabled={resendLoading}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailAwayStatusBlock;
